import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Users,
  Search,
  AlertCircle,
  Loader2,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import UserAvatar from '@/components/UserAvatar'
import { useToast } from '@/hooks/use-toast'
import { formatDate } from '@/lib/utils'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const nativeSelectClass =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20'

// Label above the control; "Optional" marks fields the form doesn't require.
const FormField = ({ id, label, optional = false, children }) => (
  <div className="space-y-1">
    <label htmlFor={id} className="flex items-baseline justify-between text-sm font-medium text-slate-700">
      {label}
      {optional && <span className="text-[13px] font-normal text-slate-400">Optional</span>}
    </label>
    {children}
  </div>
)

const ConsultantClients = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { companyUrl } = useAuthStore()
  const { states, fetchStates, isLoadingStates } = useUserStore()
  const [clientsData, setClientsData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasFetched, setHasFetched] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showCompanyClients, setShowCompanyClients] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [formError, setFormError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    gender: 'male',
    birthdate: '',
    country: 'USA',
    mobile: '',
    address: '',
    aptsuite: '',
    city: '',
    state: '',
    postalcode: '',
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
      setPage(0)
    }, 300)

    return () => clearTimeout(handler)
  }, [searchTerm])

  const loadClients = useCallback(
    async (signal, pageOverride = page, searchOverride = debouncedSearch, pageSizeOverride = pageSize) => {
      if (!companyUrl) return
      setLoading(true)
      setError(null)

      try {
        const payload = {
          fromIndex: pageOverride * pageSizeOverride,
          limit: pageSizeOverride,
          orderBy: 'DESC',
          orderByOn: 'id',
          searchtext: searchOverride,
        }

        const endpoint = showCompanyClients
          ? `/${companyUrl}/getclients`
          : `/${companyUrl}/consultantclients`

        const result = await apiService.post(endpoint, payload, signal)

        if (result.success) {
          setClientsData(result.data)
        } else {
          setError(result.error || 'Failed to load clients')
        }
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Failed to load clients')
      } finally {
        setLoading(false)
        if (!(signal?.aborted)) {
          setHasFetched(true)
        }
      }
    },
    [companyUrl, page, debouncedSearch, pageSize, showCompanyClients]
  )

  useEffect(() => {
    const controller = new AbortController()
    loadClients(controller.signal)
    return () => controller.abort()
  }, [loadClients])

  useEffect(() => {
    if (isAddModalOpen && companyUrl && states.length === 0 && !isLoadingStates) {
      fetchStates(companyUrl)
    }
  }, [isAddModalOpen, companyUrl, fetchStates, states.length, isLoadingStates])

  const dataNode = showCompanyClients ? clientsData : clientsData?.consultantclients || clientsData
  const clients = dataNode?.rows || []
  const totalCount = dataNode?.count || 0

  const isLoadingState = loading || (!hasFetched && !error)
  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1)

  const formatName = (client) => {
    const parts = [client.firstname, client.middlename, client.lastname].filter(Boolean)
    if (parts.length) return parts.join(' ')
    return client.email || 'Unnamed Client'
  }

  const formatDisplayDate = (value) => formatDate(value, { fallback: 'Not set' })

  const getMobile = (client) => {
    return client.personal?.mobilephone || client.mobilephone || client.mobile || 'Not set'
  }

  const getLocation = (client) => {
    const city = client.personal?.city || client.city
    const state = client.personal?.state || client.state
    if (city && state) return `${city}, ${state}`
    return city || state || 'Not set'
  }

  const getBirthDate = (client) => {
    return formatDisplayDate(client.personal?.birthdate || client.birthdate)
  }

  const visibleClients = clients

  const startIndex = totalCount === 0 ? 0 : page * pageSize + 1
  const endIndex = Math.min(totalCount, page * pageSize + clients.length)
  const canGoPrevious = !loading && page > 0
  const canGoNext = !loading && page + 1 < totalPages
  const rangeLabel = totalCount === 0 ? '0 - 0 of 0' : `${startIndex} - ${endIndex} of ${totalCount}`

  useEffect(() => {
    if (page >= totalPages && page > 0) {
      setPage(totalPages - 1)
    }
  }, [page, totalPages])

  const formatBirthdate = (value) => {
    if (!value) return ''
    const parts = value.split('-')
    if (parts.length !== 3) return value
    return `${parts[1]}/${parts[2]}/${parts[0]}`
  }

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const toggleClientStatus = async (client) => {
    if (!companyUrl) return
    const isActivating = !client.enabled
    const endpoint = isActivating
      ? `/${companyUrl}/companyadmin/reactiveclient/${client.id}`
      : `/${companyUrl}/companyadmin/deactiveclient/${client.id}`

    try {
      const result = await apiService.put(endpoint, {})
      if (result.success) {
        toast({
          title: `Client ${isActivating ? 'activated' : 'deactivated'}`,
          description: `${formatName(client)} has been ${isActivating ? 'activated' : 'deactivated'} successfully.`,
        })
        setClientsData((prev) => {
          if (!prev) return prev
          const updateRows = (rows) => rows.map((r) => (String(r.id) === String(client.id) ? { ...r, enabled: isActivating ? 1 : 0 } : r))

          if (showCompanyClients) {
            const currentRows = prev.rows || prev.data?.rows || []
            return { ...prev, rows: updateRows(currentRows) }
          } else {
            const consultantClients = prev.consultantclients || prev
            const currentRows = consultantClients.rows || consultantClients.data?.rows || []
            const updatedConsultantClients = { ...consultantClients, rows: updateRows(currentRows) }
            return prev.consultantclients ? { ...prev, consultantclients: updatedConsultantClients } : updatedConsultantClients
          }
        })
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update client status',
          variant: 'destructive',
        })
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update client status',
        variant: 'destructive',
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError(null)

    // Clients need a full address (leads don't); apartment/suite stays optional.
    const required = ['email', 'firstname', 'lastname', 'birthdate', 'address', 'city', 'state', 'postalcode']
    if (required.some((key) => !String(formData[key] || '').trim())) {
      setFormError('Please fill all required fields.')
      return
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(formData.email)) {
      setFormError('Please enter a valid email.')
      return
    }

    if (!companyUrl) {
      setFormError('Missing company context.')
      return
    }

    setIsSubmitting(true)

    try {
      const createResult = await apiService.post(`/${companyUrl}/createclient`, {
        email: formData.email,
        company_id: null,
      })

      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create client')
      }

      const createdId =
        createResult.data?.user_id ||
        createResult.data?.id ||
        createResult.data?.user?.id ||
        createResult.data?.client_id

      if (!createdId) {
        throw new Error('Client created but id was not returned')
      }

      const updatePayload = {
        email: formData.email,
        gender: formData.gender === 'female' ? 'f' : 'm',
        firstname: formData.firstname,
        lastname: formData.lastname,
        birthdate: formatBirthdate(formData.birthdate),
        mobilephone: formData.mobile,
        address: formData.address,
        aptsuite: formData.aptsuite,
        city: formData.city,
        state: formData.state,
        postalcode: formData.postalcode,
        user_id: createdId,
      }

      const updateResult = await apiService.put(`/${companyUrl}/updateclient`, updatePayload)

      if (!updateResult.success) {
        throw new Error(updateResult.error || 'Failed to update client')
      }

      setIsAddModalOpen(false)
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        gender: 'male',
        birthdate: '',
        country: 'USA',
        mobile: '',
        address: '',
        aptsuite: '',
        city: '',
        state: '',
        postalcode: '',
      })
      setPage(0)
      await loadClients(undefined, 0, debouncedSearch, pageSize)
    } catch (err) {
      setFormError(err?.message || 'Failed to save client')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="app-page">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <h1 className="text-[1.625rem] font-semibold leading-tight tracking-[-0.025em] text-slate-900">Clients</h1>
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogTrigger asChild>
            <Button>Add a client</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add client</DialogTitle>
              <DialogDescription>They&apos;ll be added to your client list.</DialogDescription>
            </DialogHeader>
            <form className="space-y-5" onSubmit={handleSubmit}>
              <fieldset className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                <legend className="sr-only">About the client</legend>
                <FormField id="client-firstname" label="First name">
                  <Input
                    id="client-firstname"
                    autoComplete="off"
                    value={formData.firstname}
                    onChange={(e) => handleFormChange('firstname', e.target.value)}
                    required
                  />
                </FormField>
                <FormField id="client-lastname" label="Last name">
                  <Input
                    id="client-lastname"
                    autoComplete="off"
                    value={formData.lastname}
                    onChange={(e) => handleFormChange('lastname', e.target.value)}
                    required
                  />
                </FormField>
                <FormField id="client-email" label="Email">
                  <Input
                    id="client-email"
                    type="email"
                    autoComplete="off"
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    required
                  />
                </FormField>
                <FormField id="client-mobile" label="Mobile phone" optional>
                  <Input
                    id="client-mobile"
                    type="tel"
                    autoComplete="off"
                    value={formData.mobile}
                    onChange={(e) => handleFormChange('mobile', e.target.value)}
                  />
                </FormField>
                <FormField id="client-birthdate" label="Birth date">
                  <Input
                    id="client-birthdate"
                    type="date"
                    value={formData.birthdate}
                    onChange={(e) => handleFormChange('birthdate', e.target.value)}
                    required
                  />
                </FormField>
                <div className="space-y-1">
                  <span id="client-gender-label" className="block text-sm font-medium text-slate-700">Gender</span>
                  <div role="radiogroup" aria-labelledby="client-gender-label" className="grid h-10 grid-cols-2 gap-1 rounded-md bg-slate-100 p-1">
                    {[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }].map((option) => (
                      <label
                        key={option.value}
                        className={`flex cursor-pointer items-center justify-center rounded text-sm font-medium transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-blue-500 ${
                          formData.gender === option.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        <input
                          type="radio"
                          name="gender"
                          value={option.value}
                          checked={formData.gender === option.value}
                          onChange={(e) => handleFormChange('gender', e.target.value)}
                          className="sr-only"
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>
              </fieldset>

              <fieldset className="space-y-3 border-t border-slate-200 pt-4">
                <legend className="sr-only">Address</legend>
                <p aria-hidden="true" className="text-sm font-semibold text-slate-900">Address</p>
                <div className="grid gap-x-4 gap-y-3 sm:grid-cols-[2fr_1fr]">
                  <FormField id="client-address" label="Street address">
                    <Input
                      id="client-address"
                      autoComplete="off"
                      value={formData.address}
                      onChange={(e) => handleFormChange('address', e.target.value)}
                      required
                    />
                  </FormField>
                  <FormField id="client-aptsuite" label="Apt or suite" optional>
                    <Input
                      id="client-aptsuite"
                      autoComplete="off"
                      value={formData.aptsuite}
                      onChange={(e) => handleFormChange('aptsuite', e.target.value)}
                    />
                  </FormField>
                </div>
                <div className="grid gap-x-4 gap-y-3 sm:grid-cols-[2fr_1.4fr_1fr]">
                  <FormField id="client-city" label="City">
                    <Input
                      id="client-city"
                      autoComplete="off"
                      value={formData.city}
                      onChange={(e) => handleFormChange('city', e.target.value)}
                      required
                    />
                  </FormField>
                  <FormField id="client-state" label="State">
                    <select
                      id="client-state"
                      className={nativeSelectClass}
                      value={formData.state}
                      onChange={(e) => handleFormChange('state', e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        {isLoadingStates ? 'Loading…' : 'Select'}
                      </option>
                      {states.map((state) => (
                        <option key={state.stateCode} value={state.stateCode}>
                          {state.stateName}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField id="client-postalcode" label="ZIP code">
                    <Input
                      id="client-postalcode"
                      inputMode="numeric"
                      autoComplete="off"
                      value={formData.postalcode}
                      onChange={(e) => handleFormChange('postalcode', e.target.value)}
                      required
                    />
                  </FormField>
                </div>
              </fieldset>

              {formError && (
                <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>
              )}
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setIsAddModalOpen(false)} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
                  {isSubmitting ? 'Adding…' : 'Add client'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <label htmlFor="client-search" className="sr-only">Search clients</label>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            id="client-search"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search clients"
            className="h-10 w-full pl-9 sm:w-[280px]"
          />
        </div>
        <label className="flex items-center gap-2.5 text-sm text-slate-700">
          Show company clients
          <Switch
            checked={showCompanyClients}
            onCheckedChange={(checked) => {
              setShowCompanyClients(checked)
              setPage(0)
            }}
          />
        </label>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {error ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-red-500">
              <AlertCircle className="h-6 w-6" />
              <p className="text-sm">{error}</p>
              <Button variant="outline" size="sm" onClick={() => setPage(0)}>Retry</Button>
            </div>
          ) : isLoadingState ? (
            <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading clients…</span>
            </div>
          ) : visibleClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-500">
              <Users className="h-8 w-8" />
              <p className="text-sm font-medium text-slate-500">
                {debouncedSearch ? `No clients match "${debouncedSearch}"` : 'No clients found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="border-b border-slate-200 bg-background">
                  <tr>
                    <th className="h-10 px-4 text-left text-xs font-semibold text-slate-500">Client</th>
                    <th className="h-10 px-4 text-left text-xs font-semibold text-slate-500">Consultant</th>
                    <th className="h-10 px-4 text-left text-xs font-semibold text-slate-500">Phone</th>
                    <th className="h-10 px-4 text-left text-xs font-semibold text-slate-500">Location</th>
                    <th className="h-10 px-4 text-left text-xs font-semibold text-slate-500">Date of birth</th>
                    {showCompanyClients && (
                      <th className="h-10 px-4 text-left text-xs font-semibold text-slate-500">Company</th>
                    )}
                    <th className="h-10 px-4 text-right text-xs font-semibold text-slate-500">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleClients.map((client, index) => {
                    const displayName = formatName(client)
                    const consultantName = client.consultant
                      ? [client.consultant.firstname, client.consultant.lastname].filter(Boolean).join(' ')
                      : 'Unassigned'
                    const companyName = client.company?.name

                    return (
                      <tr
                        key={client.id}
                        className="client-row-enter cursor-pointer border-b border-slate-100 transition-colors last:border-b-0 hover:bg-background"
                        style={{ animationDelay: `${Math.min(index, 8) * 30}ms` }}
                        onClick={() => navigate(`/consultant-clients/${client.id}`)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <UserAvatar name={displayName} email={client.email} className="h-[34px] w-[34px] flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-slate-900" title={displayName}>{displayName}</div>
                              <div className="max-w-[240px] truncate text-xs text-slate-500" title={client.email}>{client.email || 'Not set'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-900">{consultantName}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-900">{getMobile(client)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-900">{getLocation(client)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-900">{getBirthDate(client)}</td>
                        {showCompanyClients && (
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-900">{companyName || 'Not set'}</td>
                        )}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end">
                            <Switch
                              checked={!!client.enabled}
                              onCheckedChange={() => toggleClientStatus(client)}
                              aria-label={`${client.enabled ? 'Deactivate' : 'Activate'} ${displayName}`}
                            />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {hasFetched && !error && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-background px-4 py-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-500">Rows per page</label>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value))
                    setPage(0)
                  }}
                >
                  <SelectTrigger className="h-9 w-[76px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-sm text-slate-500">
                  <span className="font-medium text-slate-900">{rangeLabel}</span> clients
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={!canGoPrevious}
                    onClick={() => setPage(0)}
                    aria-label="First page"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={!canGoPrevious}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="px-1 text-sm text-slate-900 tabular-nums">{page + 1} / {totalPages}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={!canGoNext}
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={!canGoNext}
                    onClick={() => setPage(Math.max(totalPages - 1, 0))}
                    aria-label="Last page"
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  )
}

export default ConsultantClients
